import { FieldValues, FormProvider, useForm } from "react-hook-form";
import { SingleEditFormRow } from "../SingleEditFormRow";
import { useAppDispatch, useAppSelector } from "../../../../../app/hooks";
import { useTranslation } from "react-i18next";
import { useCallback, useContext, useEffect, useMemo } from "react";
import { WorkPeriodType } from "../../../../orders/types/workPeriodType";
import { PeriodWorkHours } from "../../../../orders/editOrder/long/workAgreement/sections/components/PeriodWorkHours";
import { AccessRightContext } from "../../../../user/AccessRightProvider";
import { EmploymentAgreedHours } from "../../../../employment/employmentApi";
import isEmpty from "../../../../../common/helpers/isEmpty";
import { getEmploymentAgreedHours } from "../../../../orders/editOrder/long/workAgreement/helpers";
import { updateEmploymentAgreedHours } from "../../../../employment/employmentSlice";

export const WorkHours = () => {
  const dispatch = useAppDispatch();
  const { t } = useTranslation("talentProfile");

  const employment = useAppSelector((state) => state.employment.additionalInfo);

  const { canEditEmployment } = useContext(AccessRightContext);
  const canEdit = canEditEmployment(employment?.employerId);

  const formatPeriodWorkHours = useCallback(
    (agreedHours?: EmploymentAgreedHours) => {
      if (agreedHours && !isEmpty(agreedHours)) {
        const { minHours, maxHours, weeksInPeriod } = agreedHours;
        const formattedMinHours = minHours.replace(".", ",");
        const formattedMaxHours = maxHours.replace(".", ",");

        if (employment.details?.workTimeType === "VARIABLE_TIME") {
          return `${formattedMinHours} - ${formattedMaxHours} ${t("customers:contract:hours")} / ${weeksInPeriod} ${t("order:weeks")}`;
        } else {
          return `${formattedMinHours} ${t("customers:contract:hours")} / ${weeksInPeriod} ${t("order:weeks")}`;
        }
      }

      return "";
    },
    [t, employment.details?.workTimeType],
  );

  const defaultValues = useMemo(() => {
    return {
      workTimeType: employment.details?.workTimeType,
      periodWorkHours: formatPeriodWorkHours(employment.details?.agreedHours),
      periodType: employment.details?.periodType,
      minHours: employment.details?.agreedHours?.minHours,
      maxHours: employment.details?.agreedHours?.maxHours,
      weeksInPeriod: employment.details?.agreedHours?.weeksInPeriod,
    };
  }, [employment.details, formatPeriodWorkHours]);

  const methods = useForm({ defaultValues, mode: "onChange" });

  const { handleSubmit } = methods;

  useEffect(() => {
    methods.reset(defaultValues);
  }, [methods, defaultValues]);

  const saveWorkHours = (data: FieldValues) => {
    const newData = {
      employmentId: employment.employmentId,
      version: employment.details.version,
      agreedHours: getEmploymentAgreedHours(data),
    };
    dispatch(updateEmploymentAgreedHours(newData));
  };

  const onSave = () => {
    handleSubmit(saveWorkHours)();
  };

  return (
    <FormProvider {...methods}>
      <SingleEditFormRow
        label={
          employment?.details?.periodType === WorkPeriodType.Weekly
            ? t("talentProfile:weeklyWorkHours")
            : t("talentProfile:periodWorkHours")
        }
        name="periodWorkHours"
        rules={{}}
        type="TextField"
        onSave={onSave}
        canEdit={canEdit}
      >
        <PeriodWorkHours />
      </SingleEditFormRow>
    </FormProvider>
  );
};
